import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CheckoutPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { items, getSubtotal, getItemPrice, getPriceTier, clearCart } = useCart();
  const { user, loading: authLoading } = useAuth();
  const { t } = useLanguage();

  // Gate the entire checkout behind a customer login (P1) — guest checkout
  // is disabled because every order must be recorded server-side so the
  // admin sees it in the notifications bell and Orders dashboard.
  // CRITICAL: wait for the auth check to complete before redirecting.
  // Otherwise logged-in customers get bounced to /connexion on a hard refresh
  // because `user` is `undefined` while /api/auth/me is still in flight.
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/connexion', {
        state: { from: '/checkout', message: 'Veuillez vous connecter pour finaliser votre commande.' },
        replace: true,
      });
    }
  }, [user, authLoading, navigate]);

  const [formData, setFormData] = useState({
    customer_name: user?.name || '',
    customer_phone: user?.phone || '',
    customer_address: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [promoDiscount, setPromoDiscount] = useState(0);

  const promoCode = location.state?.promoCode || '';
  const subtotal = getSubtotal();
  const finalTotal = subtotal - promoDiscount;

  // Re-validate promo code to compute current discount on this subtotal
  useEffect(() => {
    if (!promoCode || subtotal <= 0) {
      setPromoDiscount(0);
      return;
    }
    let cancelled = false;
    axios
      .post(`${API_URL}/api/promo/validate`, { code: promoCode, total: subtotal })
      .then(res => {
        if (!cancelled) setPromoDiscount(res.data.discount_amount || 0);
      })
      .catch(() => {
        if (!cancelled) setPromoDiscount(0);
      });
    return () => {
      cancelled = true;
    };
  }, [promoCode, subtotal]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Prepare order items
      const orderItems = items.map(item => ({
        product_id: item.product.id,
        quantity: item.quantity
      }));

      // Pre-flight cart validation — checks stock & availability in real-time
      // so we can show a precise error (e.g. "Stock insuffisant pour X")
      // instead of a generic message after the order POST fails.
      try {
        const validation = await axios.post(`${API_URL}/api/orders/validate`, {
          items: orderItems,
        });
        if (!validation.data?.ok) {
          const issues = validation.data?.issues || [];
          const lines = issues.map(i => `• ${i.message}`).join('\n');
          setError(
            issues.length === 1
              ? issues[0].message
              : `Votre panier contient ${issues.length} problème(s) :\n${lines}`
          );
          setLoading(false);
          return;
        }
      } catch (validationError) {
        // Validation endpoint failed (network/5xx) — don't block the user,
        // let the order POST attempt and surface its own error below.
      }

      // Generate WhatsApp message
      const response = await axios.post(`${API_URL}/api/whatsapp/generate`, {
        items: orderItems,
        promo_code: promoCode || null,
        customer_name: formData.customer_name,
        customer_phone: formData.customer_phone,
        customer_address: formData.customer_address,
        notes: formData.notes
      });

      // Persist the order server-side BEFORE redirecting to WhatsApp so the
      // admin sees it in the notifications bell even if the customer never
      // returns from WhatsApp. The /api/orders endpoint requires auth — we
      // already gated this page behind a login above.
      try {
        await axios.post(`${API_URL}/api/orders`, {
          items: orderItems,
          promo_code: promoCode || null,
          customer_name: formData.customer_name,
          customer_phone: formData.customer_phone,
          customer_address: formData.customer_address,
          notes: formData.notes,
        });
      } catch (orderError) {
        // Surface the actual backend error (stock, prix non défini, etc.)
        // instead of a generic "réessayer" that hides the root cause.
        const detail = orderError.response?.data?.detail;
        let msg;
        if (typeof detail === 'string' && detail.trim()) {
          msg = detail;
        } else if (Array.isArray(detail) && detail.length) {
          msg = detail.map(e => e.msg || e).join(' — ');
        } else if (orderError.response?.status === 401) {
          msg = "Votre session a expiré. Reconnectez-vous puis réessayez.";
        } else {
          msg = "La commande n'a pas pu être enregistrée. Veuillez réessayer.";
        }
        setError(msg);
        setLoading(false);
        return;
      }

      // Clear cart and redirect to WhatsApp
      clearCart();
      
      // Use location.href for better mobile compatibility
      const whatsappUrl = response.data.whatsapp_url;
      
      // Try to open WhatsApp
      window.location.href = whatsappUrl;
      
      // Navigate to confirmation after a short delay
      setTimeout(() => {
        navigate('/confirmation');
      }, 500);
    } catch (error) {
      setError(error.response?.data?.detail || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  if (items.length === 0) {
    navigate('/panier');
    return null;
  }

  // While the auth check is still in flight we render nothing — the redirect
  // useEffect above will send unauth'd users to /connexion once it completes.
  if (authLoading || !user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-main pt-24 pb-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <p className="text-[color:var(--color-primary)] text-sm uppercase tracking-[0.3em] mb-4">{t('checkout_finalize')}</p>
          <h1 
            className="text-4xl text-heading font-light"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            {t('checkout_title')}
          </h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Order Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="input-label">{t('checkout_name')} *</label>
              <input
                type="text"
                name="customer_name"
                value={formData.customer_name}
                onChange={handleChange}
                required
                className="w-full input-field"
                placeholder=""
                data-testid="checkout-name"
              />
            </div>

            <div>
              <label className="input-label">{t('checkout_phone')} *</label>
              <input
                type="tel"
                name="customer_phone"
                value={formData.customer_phone}
                onChange={handleChange}
                required
                className="w-full input-field"
                placeholder="+32 xxx xx xx xx"
                data-testid="checkout-phone"
              />
            </div>

            <div>
              <label className="input-label">{t('checkout_address')} *</label>
              <textarea
                name="customer_address"
                value={formData.customer_address}
                onChange={handleChange}
                required
                rows={3}
                className="w-full input-field resize-none"
                placeholder="Rue, numéro, code postal, ville"
                data-testid="checkout-address"
              />
            </div>

            <div>
              <label className="input-label">{t('checkout_notes')}</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows={2}
                className="w-full input-field resize-none"
                placeholder="Instructions spéciales..."
                data-testid="checkout-notes"
              />
            </div>

            {error && (
              <p className="text-red-500 text-sm whitespace-pre-line" data-testid="checkout-error">{error}</p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-3 bg-[#25D366] text-white py-4 hover:bg-[#20BD5A] transition-colors disabled:opacity-50"
              data-testid="whatsapp-checkout-button"
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              {loading ? t('checkout_processing') : t('checkout_order_btn')}
            </button>
          </form>

          {/* Order Summary */}
          <div>
            <div className="card-surface p-6">
              <h2 
                className="text-xl text-heading font-light mb-6"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                {t('checkout_your_order')}
              </h2>

              <div className="space-y-4 mb-6">
                {items.map(item => (
                  <div key={item.product.id} className="flex gap-4">
                    <div className="w-16 h-20 bg-surface overflow-hidden flex-shrink-0">
                      <img
                        src={item.product.image_url}
                        alt={item.product.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src = 'https://images.unsplash.com/photo-1541643600914-78b084683601?w=200';
                        }}
                      />
                    </div>
                    <div className="flex-1">
                      <p className="text-heading text-sm">{item.product.name}</p>
                      <p className="text-muted text-xs">{item.quantity}x @ {getItemPrice(item)}€ ({getPriceTier(item)})</p>
                      <p className="text-[color:var(--color-primary)] text-sm mt-1">
                        {(getItemPrice(item) * item.quantity).toFixed(2)}€
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {promoCode && (
                <div className="flex items-center justify-between py-3 border-t border-theme">
                  <div className="flex items-center gap-2">
                    <span className="text-muted text-sm">Code promo :</span>
                    <span className="text-[color:var(--color-primary)] text-sm font-semibold" data-testid="checkout-promo-code">{promoCode}</span>
                  </div>
                  <span className="text-[color:var(--color-primary)] text-sm" data-testid="checkout-promo-discount">
                    -{promoDiscount.toFixed(2)}€
                  </span>
                </div>
              )}

              <div className="flex justify-between items-center pt-2">
                <span className="text-muted text-sm">Sous-total</span>
                <span className="text-body text-sm" data-testid="checkout-subtotal">
                  {subtotal.toFixed(2)}€
                </span>
              </div>
              {promoDiscount > 0 && (
                <div className="flex justify-between items-center pt-1">
                  <span className="text-muted text-sm">Réduction</span>
                  <span className="text-[color:var(--color-primary)] text-sm">-{promoDiscount.toFixed(2)}€</span>
                </div>
              )}

              <div className="flex justify-between items-center pt-4 border-t border-theme">
                <span className="text-heading">Total</span>
                <span
                  className="text-2xl text-[color:var(--color-primary)] font-light"
                  style={{ fontFamily: 'Cormorant Garamond, serif' }}
                  data-testid="checkout-total"
                >
                  {finalTotal.toFixed(2)}€
                </span>
              </div>
            </div>

            {/* Info */}
            <div className="mt-4 p-4 border border-[color:rgb(var(--primary-rgb)/0.30)] bg-[color:rgb(var(--primary-rgb)/0.05)]">
              <p className="text-body text-sm">
                <span className="text-[color:var(--color-primary)]">Info:</span> {t('checkout_info')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
